import {createPortal} from "react-dom";
import {BuildingData, BuildingReward} from "../../models/remoteConstants.model";
import {computeRewardTimeline} from "../../utils/rewardTimeline";

interface RewardTimelineDialogProps {
    reward: BuildingReward;
    building: BuildingData;
    title: string;     // typically the same summary string shown on the reward card header
    onClose(): void;
}

function RewardTimelineDialog(props: RewardTimelineDialogProps) {
    const result = computeRewardTimeline(props.reward, props.building);
    const grant = props.reward.grant;
    const muted = {color: 'rgba(255,255,255,0.66)'};

    let footerNote: string | null = null;
    if (result.truncatedByMaxLevel) {
        footerNote = `Stopped at building MaxLevel (${props.building.maxLevel}).`;
    } else if (result.truncatedByWindow) {
        footerNote = 'Visualization window of 60 turns reached — would continue beyond.';
    }

    // Render via portal to document.body so that an opacity-styled ancestor
    // (e.g. a disabled building card with opacity: 0.5) doesn't drag the
    // dialog into its stacking context and visually wash it out.
    return createPortal(
        <div className={'layout vertical overlay'} onClick={props.onClose}>
            <div className={'layout vertical gap box'}
                 style={{padding: 15, gap: 10, width: 360, maxHeight: '80vh', overflowY: 'auto'}}
                 onClick={e => e.stopPropagation()}>
                <div className={'layout horizontal center stretch'} style={{gap: 10}}>
                    <p className={'text header'}>{props.title}</p>
                    <button onClick={props.onClose}>
                        <span className={'fa-solid fa-xmark'}/>
                    </button>
                </div>
                <p style={{...muted, fontSize: 13}}>
                    Assuming conditions are met; shown in isolation if this building has other periodic rewards.
                </p>
                {result.entries.length === 0
                    ? <p style={muted}>No turns in the visualization window.</p>
                    : (
                        <div className={'layout vertical gap'} style={{gap: 4}}>
                            {result.entries.map((entry, i) => {
                                const fired = entry.amount !== null;
                                return (
                                    <div key={i} className={'layout horizontal stretch label'}
                                         style={{
                                             padding: '6px 12px',
                                             opacity: fired ? 1 : 0.5,
                                         }}>
                                        <p style={fired ? undefined : muted}>Turn {entry.turn}</p>
                                        <p style={fired ? undefined : muted}>
                                            {fired ? `+${entry.amount} ${grant.kind}` : '—'}
                                        </p>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                {footerNote && <p style={{...muted, fontSize: 12, fontStyle: 'italic'}}>{footerNote}</p>}
            </div>
        </div>,
        document.body
    );
}

export default RewardTimelineDialog;
